import { rgba } from 'polished';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import _ from 'lodash';

import { Route, Switch } from '@treams/treams-routes';
import {
	Avatar,
	Button,
	Container,
	Empty,
	Input,
	Jumbo,
	Pagination,
	Popover,
	Skeleton,
	Table,
	Tabs,
} from '@treams/treams-ui';

import { SearchIcon } from '@treams/treams-icons';
import * as graphql from 'generated/graphql';

import WorkspaceEditModal from './workspaces/WorkspaceEditModal';
import WorkspaceCreateModal from './workspaces/WorkspaceCreateModal';

const Background = styled(Box)`
	min-height: 100vh;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const PlansContainer = styled(Flex)`
	cursor: pointer;
`;

const PlanContainer = styled(Box)<{ expired?: boolean; trial?: boolean }>`
	border: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	border-radius: 3px;
	box-shadow: 0px 1px 2px ${(props) => rgba(props.theme.colors.lightBlueLight, 0.5)};

	&:hover {
		border: 1px solid ${(props) => props.theme.colors.lightBlue};
	}

	${(props) =>
		props.trial &&
		`
		border-color: ${rgba(props.theme.colors.green, 0.3)};
		color: ${props.theme.colors.greenDark};

		&:hover {
			border-color: ${rgba(props.theme.colors.green, 0.5)};
		}
	`}

	${(props) =>
		props.expired &&
		`
        border-color: ${rgba(props.theme.colors.danger, 0.3)};
        color: ${props.theme.colors.danger};

        &:hover {
            border-color: ${rgba(props.theme.colors.danger, 0.5)};
        }
    `}
`;

const SupportSSOContainer = styled(PlanContainer)`
	cursor: pointer;
`;

function Plans(props: { workspace: graphql.Workspace }) {
	const { t } = useTranslation('', { keyPrefix: 'workspaces.plans' });
	const [visible, setVisible] = useState(false);

	const plans = _.sortBy(props.workspace.plans, (plan) => {
		return plan.endDate && new Date(plan.endDate).getTime();
	}).reverse();

	return (
		<Popover.Target
			backdrop
			center
			contents={
				<WorkspaceEditModal
					workspace={props.workspace}
					onDismiss={() => setVisible(false)}
					initialTab="plan"
				/>
			}
			visible={visible}
		>
			<PlansContainer onClick={() => setVisible(true)}>
				{plans.map((plan) => (
					<PlanContainer key={plan.id} p={2} mr={1} expired={plan.isExpired} trial={plan.isTrial}>
						<strong>{t(`type.${_.camelCase(plan.type)}`)}</strong>
					</PlanContainer>
				))}
				{plans.length === 0 && <Box>No subscription</Box>}
			</PlansContainer>
		</Popover.Target>
	);
}

const TeamPortraitContainer = styled(Box)`
	width: 24px;
	height: 24px;
	border-radius: 12px;
	background: ${(props) => props.theme.colors.lightBlue};
	overflow: hidden;

	img {
		width: 24px;
		height: 24px;
		object-fit: cover;
	}
`;

function TeamPortrait(props: { name: string }) {
	const images: Record<string, string> = {
		'aafke@treams.nl': '/images/team/aafke.jpg',
		'iris@treams.nl': '/images/team/iris.jpg',
		'lucienne@treams.nl': '/images/team/lucienne.jpg',
		'mariska@treams.nl': '/images/team/mariska.png',
		'marlie@treams.nl': '/images/team/marlie.jpg',
		'sjors@treams.nl': '/images/team/sjors.jpg',
		'leal@treams.nl': '/images/team/leal.jpg',
		'tim@treams.nl': '/images/team/tim.png',
		'yvette@treams.nl': '/images/team/yvette.png',
		'lizanne@treams.nl': '/images/team/lizanne.jpg',
	};

	return (
		<TeamPortraitContainer mr={1} flexGrow={0} flexShrink={0}>
			<img src={images[props.name]} alt={props.name} />
		</TeamPortraitContainer>
	);
}

function SupportSSO(props: { workspace: graphql.Workspace }) {
	const [visible, setVisible] = useState(false);

	const user = props.workspace.user;

	if (!user) {
		return null;
	}

	return (
		<Popover.Target
			backdrop
			center
			contents={
				<WorkspaceEditModal
					workspace={props.workspace}
					onDismiss={() => setVisible(false)}
					initialTab="support"
				/>
			}
			visible={visible}
		>
			<SupportSSOContainer p={2} onClick={() => setVisible(true)}>
				{user.associatedIdentities.length > 0 ? (
					<Flex>
						{user.associatedIdentities.map((identity) => (
							<TeamPortrait key={identity.name} name={identity.name} />
						))}
					</Flex>
				) : (
					<Skeleton.Empty contents={null}>
						<Flex>
							<Skeleton.Repeat count={3}>
								<Box mr={1}>
									<Avatar.Skeleton size={24} />
								</Box>
							</Skeleton.Repeat>
						</Flex>
					</Skeleton.Empty>
				)}
			</SupportSSOContainer>
		</Popover.Target>
	);
}

function WorkspaceRow(props: { workspace: graphql.Workspace }) {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);

	return (
		<Table.Row>
			<Table.Cell basis={0.25}>
				{props.workspace.id} {` (${props.workspace.alias})`}
			</Table.Cell>
			<Table.Cell basis={0.25}>
				<Plans workspace={props.workspace} />
			</Table.Cell>
			<Table.Cell basis={0.3}>
				<Box ml={2}>
					<SupportSSO workspace={props.workspace} />
				</Box>
			</Table.Cell>
			<Table.Cell basis={0.2}>
				{!props.workspace.isArchived && (
					<Popover.Target
						backdrop
						center
						contents={
							<WorkspaceEditModal
								workspace={props.workspace}
								onDismiss={() => setVisible(false)}
								initialTab="danger"
							/>
						}
						visible={visible}
					>
						<Button
							ml={2}
							type="destructive"
							title={t('common.archive')}
							onClick={() => setVisible(true)}
						/>
					</Popover.Target>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

function WorkspacesTable(props: { isArchived?: boolean }) {
	const { t } = useTranslation();

	const [prefix, setPrefix] = useState('');
	const [visible, setVisible] = useState(false);
	const [pagination, setPagination] = useState<Pagination>({ first: 10 });

	const query = graphql.useWorkspacesQuery({
		variables: {
			filters: {
				prefix: prefix.toLowerCase(),
				isArchived: props.isArchived ?? false,
			},
			...pagination,
		},
	});

	return (
		<>
			<Flex mb={2} justifyContent="space-between">
				<Input
					icon={<SearchIcon />}
					small
					value={prefix}
					onChange={setPrefix}
					autoFocus
					placeholder={t('settings.workspaces.search.placeholder')}
				/>
				<Popover.Target
					backdrop
					center
					contents={<WorkspaceCreateModal onDismiss={() => setVisible(false)} />}
					visible={visible}
				>
					<Button ml={2} small title={t('common.add')} onClick={() => setVisible(true)} />
				</Popover.Target>
			</Flex>
			<Table>
				<Table.Header>
					<Table.Cell basis={0.25}>Workspace</Table.Cell>
					<Table.Cell basis={0.25}>Subscription</Table.Cell>
					<Table.Cell basis={0.3}>Access</Table.Cell>
					<Table.Cell basis={0.2}>Actions</Table.Cell>
				</Table.Header>
				{query.data ? (
					query.data.workspacesV2.edges.length > 0 ? (
						query.data.workspacesV2.edges.map(({ node }) => (
							<WorkspaceRow key={node.id} workspace={node as graphql.Workspace} />
						))
					) : (
						<Skeleton.Empty
							contents={
								<Empty
									icon={<SearchIcon />}
									title={t('settings.workspaces.empty.title')}
									description={t('settings.workspaces.empty.description')}
								/>
							}
						>
							<Skeleton.Repeat count={3}>
								<Table.Row>
									<Table.Cell.Skeleton basis={0.25} />
									<Table.Cell.Skeleton basis={0.25} />
									<Table.Cell.Skeleton basis={0.3} />
									<Table.Cell.Skeleton basis={0.2} />
								</Table.Row>
							</Skeleton.Repeat>
						</Skeleton.Empty>
					)
				) : (
					<Skeleton.Repeat count={3}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.25} />
							<Table.Cell.Skeleton basis={0.25} />
							<Table.Cell.Skeleton basis={0.3} />
							<Table.Cell.Skeleton basis={0.2} />
						</Table.Row>
					</Skeleton.Repeat>
				)}
			</Table>
			<Table.Paging pageInfo={query.data?.workspacesV2.pageInfo} onPaginate={setPagination} />
		</>
	);
}

export default function WorkspacesSettings() {
	const { t } = useTranslation();

	return (
		<>
			<Jumbo
				title={t('settings.workspaces.title')}
				description={t('settings.workspaces.description')}
				footer={
					<Tabs.Container>
						<Tabs.Item title="Active" path="/settings/workspaces" />
						<Tabs.Item title="Archive" path="/settings/workspaces/archive" />
					</Tabs.Container>
				}
			/>
			<Background>
				<Container py={4}>
					<Switch>
						<Route path="/settings/workspaces" exact>
							<WorkspacesTable />
						</Route>
						<Route path="/settings/workspaces/archive" exact>
							<WorkspacesTable isArchived />
						</Route>
					</Switch>
				</Container>
			</Background>
		</>
	);
}
